import { useState, useRef, useEffect, useContext } from 'react';
import TabContext from '@nile/mangifera/src/Contexts/TicketContext';
import { GLOBAL_STATE_CONFIGURATION } from './config';

class StateManager {
  constructor() {
    // Currently it is in memory. We can use different storage systems
    this.globalState = {};
  }

  useGlobalState = (key, initialState, globalState) => {
    // Get the current active tab and then store the value in the respective object of the global state.
    // We can hash the component name and key to have a unique key name across components to avoid duplication.
    // Will be easier for lookup too.
    const tabContext = useContext(TabContext);
    const storageMechanism = this.getStorageMechanism(tabContext, key);
    const isDevEnv = ['development'].includes(process.env.NODE_ENV);
    const [state, setState] = useState(() => {
      if (storageMechanism === 'memory') {
        const currentState = globalState
          ? this?.globalState?.globalState?.[key]
          : this?.globalState?.[tabContext?.activeTab?.id]?.[key];
        if (currentState || typeof currentState === 'boolean') {
          return currentState;
        } else {
          return initialState;
        }
      }
      return initialState;
    });
    const cbRef = useRef(null); // init mutable ref container for callbacks

    const setStateCallback = (s, cb) => {
      cbRef.current = cb; // store current, passed callback in ref
      return setState(s);
    }; // keep object reference stable, exactly like `useState`

    useEffect(() => {
      // Throw error if config is not there in type of the tab or if tab config is not defined
      const configType = globalState
        ? GLOBAL_STATE_CONFIGURATION?.globalState
        : this.getConfigType(tabContext);
      const isNullable = state === null && configType?.[key]?.nullable;
      // eslint-disable-next-line valid-typeof
      const isInvalidType = typeof state !== configType?.[key]?.type;
      if (!configType || !Object.hasOwn(configType, key)) {
        if (isDevEnv) {
          throw new Error(
            'Property cannot be stored in state as it is missing in the schema. Add the property in global state schema to store the Value'
          );
        } else return;
      }
      // eslint-disable-next-line valid-typeof
      if (!isNullable && isInvalidType) {
        if (isDevEnv) {
          throw new Error('Property type mismatch');
        } else return;
      }

      // Based on config, store the value in storage or in memory object. Currently it's in memory.
      // We can check for types based on the tab and then validate the value based on that.
      const activeTabId = globalState
        ? 'globalState'
        : this.getActiveTabId(tabContext);
      if (!this.globalState[activeTabId]) {
        this.createGlobalState(activeTabId, key, state);
      } else {
        this.setGlobalState(activeTabId, key, state);
      }
      if (cbRef.current) {
        cbRef.current(state);
        cbRef.current = null; // reset callback after execution
      }
    }, [state]);
    return [state, setStateCallback];
  };

  getConfigType = (context) => {
    return GLOBAL_STATE_CONFIGURATION[context?.activeTab?.module];
  };

  getActiveTabGlobalState = (context) => {
    return this.globalState[context?.activeTab?.id];
  };

  getStorageMechanism = (context, key) => {
    return (
      GLOBAL_STATE_CONFIGURATION[context?.activeTab?.module]?.[key]?.storage ||
      'memory'
    );
  };

  getActiveTabId = (context) => {
    return context?.activeTab?.id;
  };

  getState = (key) => {
    return this.globalState[key];
  };

  createGlobalState = (activeTabId, key, state) => {
    this.globalState[activeTabId] = {};
    this.globalState[activeTabId][key] = state;
  };

  // set the value to storage system. If it's local storage, we need to serialize and set.
  setGlobalState = (activeTabId, key, state) => {
    this.globalState[activeTabId][key] = state;
  };

  clearState = (key) => {
    if (this.globalState[key]) {
      delete this.globalState[key];
    }
  };

  getObjectMemory = () => {
    const jsonString = JSON.stringify(this.globalState);
    const sizeInBytes = new Blob([jsonString]).size;
    const kbMemory = `${(sizeInBytes / 1024).toFixed(2)} KB`;
    return kbMemory;
  };
}

const GlobalStateManager = new StateManager();

const {
  useGlobalState,
  getState,
  clearState,
  getObjectMemory,
  setGlobalState
} = GlobalStateManager;

export {
  useGlobalState,
  getState,
  clearState,
  getObjectMemory,
  setGlobalState
};
